/**
 * DisclosureTriangle Web Component
 * 
 * A collapsible disclosure widget with animated triangle indicator.
 * 
 * Usage:
 *   <disclosure-triangle>
 *     <span slot="label">Click to expand</span>
 *     <p>Hidden content goes here</p>
 *   </disclosure-triangle>
 * 
 * Attributes:
 *   - open: Start in open state (boolean attribute)
 *   - label: Text label (alternative to slot)
 *   - duration: Animation duration in ms (default: 300)
 */

const STYLES = `
  :host {
    display: block;
  }
  
  .label-row {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }
  
  .label-row:focus-visible {
    outline: 2px solid #8b7355;
    outline-offset: 2px;
  }
  
  svg {
    flex-shrink: 0;
    vertical-align: middle;
    width: 0.5cm;
    height: 0.5cm;
    fill: currentColor;
  }
  
  svg:hover {
    opacity: 0.7;
  }
  
  #triangle {
    transition: transform var(--duration, 300ms) ease-in-out;
    transform-origin: 5px 5px;
  }
  
  #triangle.rotate {
    transform: rotate(90deg);
  }
  
  #label {
    margin-left: 0.25em;
  }
  
  #container {
    overflow: hidden;
    transition: height var(--duration, 300ms) ease-in-out;
  }
  
  #container.collapsed {
    height: 0 !important;
  }
  
  #container.no-transition {
    transition: none;
  }
  
  #content {
    /* Padding to prevent margin collapse issues */
  }
  
  /* Touch device adjustments */
  @media (hover: none) {
    svg:hover {
      opacity: 1;
    }
    
    svg:active {
      opacity: 0.7;
    }
  }
`;

class DisclosureTriangle extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._isAnimating = false;
  }
  
  connectedCallback() {
    this._render();
    queueMicrotask(() => this._setup());
  }
  
  _render() {
    const labelAttr = this.getAttribute('label');
    
    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div>
        <div class="label-row" part="label-row" tabindex="0" role="button" aria-expanded="false">
          <svg viewBox="0 0 10 10" part="triangle" aria-hidden="true">
            <polygon id="triangle" points="2,1 8,5 2,9" />
          </svg>
          <span id="label" part="label">${labelAttr ? this._escapeHtml(labelAttr) : '<slot name="label"></slot>'}</span>
        </div>
        <div id="container" role="region">
          <div id="content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
  
  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  _setup() {
    this._applyDuration();
    this._setInitialState();
    this._addEventListeners();
  }
  
  _setInitialState() {
    const container = this.shadowRoot.getElementById('container');
    const triangle = this.shadowRoot.getElementById('triangle');
    const labelRow = this.shadowRoot.querySelector('.label-row');
    
    if (!this.hasAttribute('open')) {
      // Start collapsed without animation
      container.classList.add('no-transition', 'collapsed');
      container.style.height = '0';
      labelRow.setAttribute('aria-expanded', 'false');
      
      // Remove no-transition after a frame to enable future animations
      requestAnimationFrame(() => {
        container.classList.remove('no-transition');
      });
    } else {
      triangle.classList.add('rotate');
      labelRow.setAttribute('aria-expanded', 'true');
    }
  }
  
  _addEventListeners() {
    const labelRow = this.shadowRoot.querySelector('.label-row');
    
    labelRow.addEventListener('click', () => this.toggle());
    
    labelRow.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    });
  }
  
  _applyDuration() {
    const duration = this.getAttribute('duration');
    if (duration !== null) {
      this.style.setProperty('--duration', `${duration}ms`);
    }
  }
  
  _animateOpen() {
    const container = this.shadowRoot.getElementById('container');
    const content = this.shadowRoot.getElementById('content');
    const triangle = this.shadowRoot.getElementById('triangle');
    const labelRow = this.shadowRoot.querySelector('.label-row');
    
    if (this._isAnimating) return;
    this._isAnimating = true;
    
    // Rotate triangle
    triangle.classList.add('rotate');
    labelRow.setAttribute('aria-expanded', 'true');
    
    // Get the target height
    const targetHeight = content.offsetHeight;
    
    // Set explicit height for animation
    container.style.height = '0';
    container.classList.remove('collapsed');
    
    // Force reflow
    void container.offsetHeight;
    
    // Animate to target height
    container.style.height = targetHeight + 'px';
    
    // Clean up after animation
    const duration = parseInt(this.getAttribute('duration')) || 300;
    setTimeout(() => {
      container.style.height = '';  // Allow natural height
      this._isAnimating = false;
    }, duration);
  }
  
  _animateClose() {
    const container = this.shadowRoot.getElementById('container');
    const triangle = this.shadowRoot.getElementById('triangle');
    const labelRow = this.shadowRoot.querySelector('.label-row');
    
    if (this._isAnimating) return;
    this._isAnimating = true;
    
    // Rotate triangle back
    triangle.classList.remove('rotate');
    labelRow.setAttribute('aria-expanded', 'false');
    
    // Set explicit current height for animation start point
    container.style.height = container.offsetHeight + 'px';
    
    // Force reflow
    void container.offsetHeight;
    
    // Animate to zero
    container.style.height = '0';
    
    // Add collapsed class after animation
    const duration = parseInt(this.getAttribute('duration')) || 300;
    setTimeout(() => {
      container.classList.add('collapsed');
      this._isAnimating = false;
    }, duration);
  }
  
  // === Public API ===
  
  /**
   * Toggle the disclosure state
   */
  toggle() {
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
  }
  
  /**
   * Open the disclosure
   */
  open() {
    this.setAttribute('open', '');
  }
  
  /**
   * Close the disclosure
   */
  close() {
    this.removeAttribute('open');
  }
  
  /**
   * Check if currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.hasAttribute('open');
  }
  
  // === End Public API ===
  
  static get observedAttributes() {
    return ['label', 'open', 'duration'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    // Guard against calls before shadowRoot is populated
    if (!this.shadowRoot || !this.shadowRoot.getElementById('container')) {
      return;
    }
    
    if (name === 'label') {
      const labelSpan = this.shadowRoot.getElementById('label');
      if (labelSpan) {
        if (newValue !== null) {
          labelSpan.textContent = newValue;
        } else {
          // Restore slot if label attribute is removed
          labelSpan.innerHTML = '<slot name="label"></slot>';
        }
      }
    }
    
    if (name === 'open') {
      // newValue is '' when attribute is set, null when removed
      if (newValue !== null) {
        this._animateOpen();
      } else {
        this._animateClose();
      }
    }
    
    if (name === 'duration') {
      this._applyDuration();
    }
  }
}

customElements.define('disclosure-triangle', DisclosureTriangle);

export { DisclosureTriangle };
export default DisclosureTriangle;
